import { create } from 'zustand';
import type {
  CommentForMedia,
  MediaType,
  OrderDirection,
  TeamCommentForMedia,
  TeamCommentsOrderField,
} from '~/graphql';
import type { Nullable } from '~/utilities/type-guards';
import { isNotNil } from '~/utilities/type-guards';
import type { TopLevelComment } from '../Comments/types';
import type { MediaQuery } from '../__gql/Media.gql.gen';
import type { CommentTabName } from '../Comments/CommentTabs';
import type { CommentingViewsType } from '../Comments/CommentContext';
import type { Ev1Player } from '../../../../../types/e-v1-player-api-types';
import type { TimelineItemCategory, TimelineItemType } from '../Timeline/types/TimelineItem';
import type { PlayerStickyPosition } from '../Player/types/PlayerStickyPosition';
import type { MediaPageTabName } from '../TabbedArea/types/MediaPageTabName';
import { flattenCommentEdges } from '../utils/flattenCommentEdges';

type ActiveComment = Pick<TopLevelComment, 'id' | 'mediaTimestamp'> | null;
type SelectedTimelineItem = Pick<TimelineItemType, 'category' | 'id' | 'time'> | null;
type TabbedAreaPositionType = 'auto' | 'bottom' | 'sidecar';

type OptionsForSetActiveCommentId = Nullable<{
  shouldNavigateToCommentTab: boolean;
  commentTab?: CommentingViewsType;
}>;

export type CommentingSortType = {
  field: TeamCommentsOrderField;
  direction: OrderDirection;
};

export const commentingSorts: Record<string, CommentingSortType> = {
  timecode: { field: 'MEDIA_TIMESTAMP', direction: 'ASC' },
  newest: { field: 'CREATED_AT', direction: 'DESC' },
  oldest: { field: 'CREATED_AT', direction: 'ASC' },
};

type MediaStoreActionsType = {
  setHideResolvedComments: (hideResolvedComments: boolean) => void;
  setTopLevelTeamComments: (topLevelTeamComments: MediaStoreType['topLevelTeamComments']) => void;
  setTopLevelAudienceComments: (
    topLevelAudienceComments: MediaStoreType['topLevelAudienceComments'],
  ) => void;
  initializeFromMedia: (media: NonNullable<MediaQuery['media']>) => void;
  setActiveComment: (
    comment: MediaStoreType['activeComment'],
    options?: OptionsForSetActiveCommentId,
  ) => void;
  setActiveTab: (tab: MediaPageTabName) => void;
  setActiveCommentTab: (tab: CommentTabName) => void;
  setSelectedTimelineItem: (timelineItem: MediaStoreType['selectedTimelineItem']) => void;
  setPlayer: (player: MediaStoreType['player']) => void;
  setHighlightedTimelineItemCategory: (
    category: MediaStoreType['highlightedTimelineItemCategory'],
  ) => void;
  setStickyPlayerPosition: (playerStickyPosition: MediaStoreType['playerStickyPosition']) => void;
  setStickyPlayerPhantomHeight: (
    stickyPlayerPhantomHeight: MediaStoreType['stickyPlayerPhantomHeight'],
  ) => void;
  setTabbedAreaPosition: (position: TabbedAreaPositionType) => void;
  setCommentingSort: (commentingSort: CommentingSortType) => void;
};

export type MediaStoreType = {
  activeComment: ActiveComment;
  activeCommentTab: CommentingViewsType;
  activeTab: MediaPageTabName;
  highlightedTimelineItemCategory: TimelineItemCategory | null;
  mediaHashedId: string;
  mediaGraphQLId: string;
  mediaType: MediaType;
  mediaStoreActions: MediaStoreActionsType;
  player: Ev1Player | null;
  playerStickyPosition: PlayerStickyPosition;
  stickyPlayerPhantomHeight: number;
  selectedTimelineItem: SelectedTimelineItem;
  topLevelTeamComments: TopLevelComment[];
  topLevelAudienceComments: TopLevelComment[];
  tabbedAreaPosition: TabbedAreaPositionType;
  commentingSort: CommentingSortType;
  hideResolvedComments: boolean;
  thumbnailAssetUrl: string | null;
};

const DEFAULT_CONTEXT_VALUE: Omit<MediaStoreType, 'mediaStoreActions'> = {
  activeComment: null,
  activeCommentTab: 'Team' as const,
  activeTab: 'Transcript',
  commentingSort: commentingSorts.timecode,
  hideResolvedComments: true,
  highlightedTimelineItemCategory: null,
  mediaGraphQLId: '',
  mediaHashedId: '',
  mediaType: 'Video',
  player: null,
  playerStickyPosition: 'none',
  selectedTimelineItem: null,
  stickyPlayerPhantomHeight: 0,
  tabbedAreaPosition: 'auto',
  topLevelAudienceComments: [],
  topLevelTeamComments: [],
  thumbnailAssetUrl: null,
};

export const useMediaStore = create<MediaStoreType>()((set) => {
  const setHideResolvedComments = (hideResolvedComments: boolean) => {
    set((state) => ({
      ...state,
      hideResolvedComments,
    }));
  };
  const setCommentingSort = (commentingSort: CommentingSortType) => {
    set((state) => ({
      ...state,
      commentingSort,
    }));
  };

  const setTopLevelTeamComments = (topLevelTeamComments: TopLevelComment[]) => {
    set((state) => ({
      ...state,
      topLevelTeamComments,
    }));
  };

  const setTopLevelAudienceComments = (topLevelAudienceComments: TopLevelComment[]) => {
    set((state) => ({
      ...state,
      topLevelAudienceComments,
    }));
  };

  const initializeFromMedia = (media: NonNullable<MediaQuery['media']>) => {
    setTopLevelTeamComments(
      flattenCommentEdges(media.topLevelTeamComments.edges as TeamCommentForMedia[]),
    );
    setTopLevelAudienceComments(
      flattenCommentEdges(media.topLevelAudienceComments.edges as CommentForMedia[]),
    );

    set((state) => ({
      ...state,
      mediaHashedId: media.hashedId,
      mediaGraphQLId: media.id,
      thumbnailAssetUrl: media.thumbnailUrlLarge,
      mediaType: media.mediaType,
    }));
  };

  const setActiveTab = (activeTab: MediaPageTabName) => {
    set((state) => ({
      ...state,
      activeTab,
    }));
  };

  const setActiveCommentTab = (activeCommentTab: CommentTabName) => {
    set((state) => ({
      ...state,
      activeCommentTab,
    }));
  };

  const setActiveComment = (
    activeComment: ActiveComment,
    options: OptionsForSetActiveCommentId,
  ) => {
    if (options?.shouldNavigateToCommentTab && isNotNil(options.commentTab)) {
      setActiveTab('Comments');
      setActiveCommentTab(options.commentTab);
    }

    set((state) => ({
      ...state,
      activeComment,
    }));
  };

  const setHighlightedTimelineItemCategory = (
    highlightedTimelineItemCategory: MediaStoreType['highlightedTimelineItemCategory'],
  ) => {
    set((state) => ({
      ...state,
      highlightedTimelineItemCategory,
    }));
  };

  const setSelectedTimelineItem = (selectedTimelineItem: SelectedTimelineItem) => {
    setHighlightedTimelineItemCategory(selectedTimelineItem?.category ?? null);
    set((state) => ({
      ...state,
      selectedTimelineItem,
    }));
  };

  const setPlayer = (player: MediaStoreType['player']) => {
    set((state) => ({
      ...state,
      player,
    }));
  };

  const setStickyPlayerPosition = (
    playerStickyPosition: MediaStoreType['playerStickyPosition'],
  ) => {
    set((state) => ({
      ...state,
      playerStickyPosition,
    }));
  };

  const setStickyPlayerPhantomHeight = (
    stickyPlayerPhantomHeight: MediaStoreType['stickyPlayerPhantomHeight'],
  ) => {
    set((state) => ({
      ...state,
      stickyPlayerPhantomHeight,
    }));
  };

  const setTabbedAreaPosition = (tabbedAreaPosition: TabbedAreaPositionType) => {
    set((state) => ({
      ...state,
      tabbedAreaPosition,
    }));
  };

  return {
    ...DEFAULT_CONTEXT_VALUE,
    mediaStoreActions: {
      setTopLevelTeamComments,
      setTopLevelAudienceComments,
      initializeFromMedia,
      setActiveComment,
      setActiveTab,
      setActiveCommentTab,
      setSelectedTimelineItem,
      setPlayer,
      setStickyPlayerPosition,
      setStickyPlayerPhantomHeight,
      setHighlightedTimelineItemCategory,
      setTabbedAreaPosition,
      setCommentingSort,
      setHideResolvedComments,
    },
  };
});

export const MediaStoreSelectors = {
  getTopLevelTeamComments: (store: MediaStoreType): MediaStoreType['topLevelTeamComments'] =>
    store.topLevelTeamComments,
  getTopLevelAudienceComments: (
    store: MediaStoreType,
  ): MediaStoreType['topLevelAudienceComments'] => store.topLevelAudienceComments,
  getActiveTab: (store: MediaStoreType): MediaStoreType['activeTab'] => store.activeTab,
  getActiveCommentTab: (store: MediaStoreType): MediaStoreType['activeCommentTab'] =>
    store.activeCommentTab,
  getActiveComment: (store: MediaStoreType): MediaStoreType['activeComment'] => store.activeComment,
  getSelectedTimelineItem: (store: MediaStoreType): MediaStoreType['selectedTimelineItem'] =>
    store.selectedTimelineItem,
  getMediaHashedId: (store: MediaStoreType): MediaStoreType['mediaHashedId'] => store.mediaHashedId,
  getMediaGraphQLId: (store: MediaStoreType): MediaStoreType['mediaGraphQLId'] =>
    store.mediaGraphQLId,
  getPlayer: (store: MediaStoreType): MediaStoreType['player'] => store.player,
  getPlayerStickyPosition: (store: MediaStoreType): MediaStoreType['playerStickyPosition'] =>
    store.playerStickyPosition,
  getStickyPlayerPhantomHeight: (
    store: MediaStoreType,
  ): MediaStoreType['stickyPlayerPhantomHeight'] => store.stickyPlayerPhantomHeight,
  getActions: (store: MediaStoreType): MediaStoreActionsType => store.mediaStoreActions,
  getHighlightedTimelineItemCategory: (
    store: MediaStoreType,
  ): MediaStoreType['highlightedTimelineItemCategory'] => store.highlightedTimelineItemCategory,
  getTabbedAreaPosition: (store: MediaStoreType): MediaStoreType['tabbedAreaPosition'] =>
    store.tabbedAreaPosition,
  getCommentingSort: (store: MediaStoreType): MediaStoreType['commentingSort'] =>
    store.commentingSort,
  getHideResolvedComments: (store: MediaStoreType): MediaStoreType['hideResolvedComments'] =>
    store.hideResolvedComments,
  getMediaType: (store: MediaStoreType): MediaStoreType['mediaType'] => store.mediaType,
};
